import { createMachine, assign, setup } from "xstate";
import { ExemptionQuestions } from "utils/types/exemption-questions";
import { commonGuards } from "./_commonGuards";

export const texasExemptionQuestionFlowMachine = setup({
	types: {
		context: {} as ExemptionQuestions.FlowContext,
		events: {} as ExemptionQuestions.FlowEvent,
		input: {} as Partial<ExemptionQuestions.FlowContext>,
	},
	actions: {
		updateContext: assign(({ context, event }) => {
			if ("context" in event) {
				return {
					...context,
					...event.context,
				};
			}
			return context;
		}),
	},
	guards: {},
	delays: {},
}).createMachine({
	id: "TEXAS_EXEMPTION",
	context: ({ input }) => ({ ...input }) as ExemptionQuestions.FlowContext,
	initial: "IDLE",
	states: {
		IDLE: {},
		INIT: {
			always: [
				// {
				// 	target: "INTRO",
				// 	guard: ({ context, event }) => {
				// 		const url = new URL(window.location.href);

				// 		return (
				// 			context.primaryResidenceValue === undefined &&
				// 			url.searchParams.get("q") !== "primaryResidence"
				// 		);
				// 	},
				// },
				{
					target: "PRIMARY_RESIDENCE",
					guard: ({ context, event }) => {
						return context.primaryResidenceValue === undefined;
					},
				},
				{
					target: "CURRENTLY_RECEIVING",
					guard: ({ context, event }) =>
						context.currentlyReceivingValue === undefined,
				},
				{
					target: "MOVE_IN_DATE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.moveInDateValue === undefined,
				},
				{
					target: "HOMESTEAD_QUALIFIED",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.moveInDateValue === undefined,
				},
				{
					target: "ALREADY_FILED",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue?.already_filed === undefined,
				},
				{
					target: "RARE_SITUATIONS",
					guard: ({ context, event }) => {
						return (
							commonGuards.currentlyReceivingNoOrNotSure(context) &&
							[
								context.hasDisability === true,
								context.disabledVeteranOrSurvivingSpouse === true,
								context.survivingSpouseOf65PlusPerson === true,
								context.survivingSpouseOfVeteranKilledInLineOfDuty === true,
								context.survivingSpouseOfFirstResponderKilledInLineOfDuty ===
									true,
								context.isInheritedProperty === true,
								context.donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse ===
									true,
								context.ownedThroughCooperativeHousing === true,
								context.noneOfTheAbove === undefined,
								context.specialFieldsValue?.is_active_duty_personnel ===
									undefined,
								context.specialFieldsValue?.is_manufactured_home === true,
								context.collectRentAtThisProperty === null,
							].some(value => value === true)
						);
					},
				},
				{
					target: "SQFT",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.collectRentAtThisProperty === true,
							context.sqFtIncomeProducing === undefined,
						].every(value => value === true),
				},
				{
					target: "PURCHASE_DATE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.purchaseDateValue === undefined,
				},
				{
					target: "DOB",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.dobValue === undefined,
				},
				{
					target: "PREVIOUS_RESIDENCE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.previousAddressValue === undefined,
				},
				{
					target: "OWNERS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.ownersValue === undefined,
				},
				{
					target: "ADDITIONAL_OWNER",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.ownersValue === "Other Persons",
							context.ownersValue === "Own With Spouse",
						].some(value => value === true) &&
						[
							,
							context.additionalOwnerFirstNameValue === undefined,
							context.additionalOwnerLastNameValue === undefined,
							context.additionalOwnerDobValue === undefined,
							context.additionalOwnerEmailValue === undefined,
							context.additionalOwnerPhoneValue === undefined,
						].some(value => value === true),
				},
				{
					target: "OWNERSHIP_PERCENT",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.ownersValue === "Other Persons",
							context.ownersValue === "Own With Spouse",
						].some(value => value === true) &&
						[
							,
							context.ownerOneValue === undefined,
							context.ownerTwoValue === undefined,
						].some(value => value === true),
				},
				{
					target: "DL_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.ownersValue === "Sole Owner" &&
						!context.ownerHasUploadedDlFile,
				},
				{
					target: "DL2_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.ownersValue === "Other Persons",
							context.ownersValue === "Own With Spouse",
						].some(value => value === true) &&
						!!context.exemption_requires_both_licenses &&
						!!context.ownerHasUploadedDlFile &&
						!context.owner2HasUploadedDlFile,
				},
				{
					target: "TRUST_DOCUMENT_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.auth_company_type === "Trust" &&
						!context.specialFieldsValue?.trust_document_ref,
				},
				{
					target: "AGREEMENT",
					guard: ({ context, event }) =>
						[
							context.primaryResidenceValue === true,
							commonGuards.currentlyReceivingNoOrNotSure(context),
						].every(value => value === true),
				},
			],
		},
		// INTRO: {
		// 	entry: [
		// 		assign({
		// 			exemptionsProgress: context => 0,
		// 		}),
		// 	],
		// 	on: {
		// 		next: {
		// 			target: "PRIMARY_RESIDENCE",
		// 		},
		// 	},
		// },
		PRIMARY_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => (1 / 19) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "CURRENTLY_RECEIVING",
						guard: ({ context, event }) =>
							commonGuards.isPrimaryResidence(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							commonGuards.isNotPrimaryResidence(context),
					},
				],
				// back: {
				// 	target: "INTRO",
				// 	actions: assign({
				// 		primaryResidenceValue: context => undefined,
				// 	}),
				// },
			},
		},
		NON_AGREEMENT_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
		},
		CURRENTLY_RECEIVING: {
			entry: [
				assign({
					exemptionsProgress: context => (2 / 19) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingYes(context),
					},
					{
						target: "MOVE_IN_DATE",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
					},
				],
				back: {
					target: "PRIMARY_RESIDENCE",
					actions: assign({
						currentlyReceivingValue: context => undefined,
					}),
				},
			},
		},
		MOVE_IN_DATE: {
			entry: [
				assign({
					exemptionsProgress: context => (3 / 19) * 100,
				}),
			],
			on: {
				next: {
					target: "HOMESTEAD_QUALIFIED",
				},
				back: {
					target: "CURRENTLY_RECEIVING",
					actions: assign({
						moveInDateValue: context => undefined,
						retroSavingsAmount: context => undefined,
						futureSavingsAmount: context => undefined,
					}),
				},
			},
		},
		HOMESTEAD_QUALIFIED: {
			entry: [
				assign({
					exemptionsProgress: context => (4 / 19) * 100,
				}),
			],
			on: {
				back: {
					target: "MOVE_IN_DATE",
				},
				next: [
					{
						target: "ALREADY_FILED",
					},
				],
			},
		},
		ALREADY_FILED: {
			entry: [
				assign({
					exemptionsProgress: context => (5 / 19) * 100,
				}),
			],
			on: {
				back: {
					target: "HOMESTEAD_QUALIFIED",
					actions: assign({
						specialFieldsValue: ({ context }) => ({
							...context.specialFieldsValue,
							already_filed: undefined,
						}),
					}),
				},
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							context.specialFieldsValue?.already_filed === true,
					},
					{
						target: "RARE_SITUATIONS",
					},
				],
			},
		},
		RARE_SITUATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (6 / 19) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							context.hasDisability === true ||
							context.disabledVeteranOrSurvivingSpouse === true ||
							context.survivingSpouseOf65PlusPerson === true ||
							context.survivingSpouseOfVeteranKilledInLineOfDuty === true ||
							context.survivingSpouseOfFirstResponderKilledInLineOfDuty ===
								true ||
							context.isInheritedProperty === true ||
							context.donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse ===
								true ||
							context.specialFieldsValue?.is_manufactured_home === true ||
							context.ownedThroughCooperativeHousing === true,
					},
					{
						target: "SQFT",
						guard: ({ context, event }) =>
							context.collectRentAtThisProperty === true,
					},
					{
						target: "PURCHASE_DATE",
						guard: ({ context, event }) =>
							commonGuards.rareSituationsAbleToFile(context),
					},
				],
				back: {
					target: "ALREADY_FILED",
					actions: assign({
						hasDisability: context => undefined,
						collectRentAtThisProperty: context => undefined,
						disabledVeteranOrSurvivingSpouse: context => undefined,
						survivingSpouseOf65PlusPerson: context => undefined,
						survivingSpouseOfVeteranKilledInLineOfDuty: context => undefined,
						survivingSpouseOfFirstResponderKilledInLineOfDuty: context =>
							undefined,
						isInheritedProperty: context => undefined,
						otherHeirOwnersOccupy: context => undefined,
						donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse: context =>
							undefined,
						ownedThroughCooperativeHousing: context => undefined,
						noneOfTheAbove: context => undefined,
						specialFieldsValue: ({ context }) => ({
							...context.specialFieldsValue,
							is_active_duty_personnel: undefined,
						}),
					}),
				},
			},
		},
		SQFT: {
			entry: [
				assign({
					exemptionsProgress: context => (7 / 19) * 100,
				}),
			],
			on: {
				next: {
					target: "PURCHASE_DATE",
				},
				back: {
					target: "RARE_SITUATIONS",
					actions: assign({ sqFtIncomeProducing: context => undefined }),
				},
			},
		},
		PURCHASE_DATE: {
			entry: [
				assign({
					exemptionsProgress: context => (8 / 19) * 100,
				}),
			],
			on: {
				next: {
					target: "DOB",
				},
				back: {
					target: "RARE_SITUATIONS",
					actions: assign({
						purchaseDateValue: context => undefined,
					}),
				},
			},
		},
		DOB: {
			entry: [
				assign({
					exemptionsProgress: context => (9 / 19) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "PREVIOUS_RESIDENCE",
					},
				],
				back: {
					target: "PURCHASE_DATE",
					actions: assign({ dobValue: context => undefined }),
				},
			},
		},
		PREVIOUS_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => (10 / 19) * 100,
				}),
			],
			on: {
				back: {
					target: "DOB",
					actions: assign({
						previousAddressValue: context => undefined,
						previousAddress2Value: context => undefined,
						previousCityValue: context => undefined,
						previousStateValue: context => undefined,
						previousZipcodeValue: context => undefined,
					}),
				},
				next: {
					target: "OWNERS",
				},
			},
		},
		OWNERS: {
			entry: [
				assign({
					exemptionsProgress: context => (11 / 19) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "DL_UPLOAD",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
					},
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
					},
				],
				back: [
					{
						target: "PREVIOUS_RESIDENCE",
						actions: assign({ ownersValue: context => undefined }),
					},
				],
			},
		},
		ADDITIONAL_OWNER: {
			entry: [
				assign({
					exemptionsProgress: context => (12 / 19) * 100,
				}),
			],
			on: {
				next: {
					target: "OWNERSHIP_PERCENT",
				},
				back: {
					target: "OWNERS",
					actions: assign({
						additionalOwnerFirstNameValue: context => undefined,
						additionalOwnerLastNameValue: context => undefined,
						additionalOwnerEmailValue: context => undefined,
						additionalOwnerPhoneValue: context => undefined,
						additionalOwnerDobValue: context => undefined,
					}),
				},
			},
		},
		OWNERSHIP_PERCENT: {
			entry: [
				assign({
					exemptionsProgress: context => (13 / 19) * 100,
				}),
			],
			on: {
				next: {
					target: "DL_UPLOAD",
				},
				back: {
					target: "ADDITIONAL_OWNER",
					actions: assign({
						ownerOneValue: context => undefined,
						ownerTwoValue: context => undefined,
					}),
				},
			},
		},
		DL_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (14 / 19) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context) &&
							!!context.exemption_requires_both_licenses,
					},
					{
						target: "TRUST_DOCUMENT_UPLOAD",
						guard: ({ context, event }) =>
							context.auth_company_type === "Trust",
					},
					{
						target: "OTHER_COUNTIES",
					},
				],
				back: [
					{
						target: "OWNERS",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
				],
			},
		},
		DL2_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (15 / 19) * 100,
				}),
			],
			on: {
				next: [
					{
						target: "TRUST_DOCUMENT_UPLOAD",
						guard: ({ context, event }) =>
							context.auth_company_type === "Trust",
					},
					{
						target: "OTHER_COUNTIES",
					},
				],
				back: {
					target: "DL_UPLOAD",
					actions: assign({
						owner2DlFilesToUpload: context => [],
						owner2DlExemptValue: context => undefined,
					}),
				},
			},
		},
		TRUST_DOCUMENT_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (16 / 19) * 100,
				}),
			],
			on: {
				next: {
					target: "OTHER_COUNTIES",
				},
				back: [
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context) &&
							!!context.exemption_requires_both_licenses,
						actions: assign({
							otherCountiesValue: context => undefined,
						}),
					},
					{
						target: "DL_UPLOAD",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
						actions: assign({
							otherCountiesValue: context => undefined,
						}),
					},
				],
			},
		},
		OTHER_COUNTIES: {
			entry: [
				assign({
					exemptionsProgress: context => (17 / 19) * 100,
				}),
			],
			on: {
				next: {
					target: "AGREEMENT",
				},
				back: [
					{
						target: "TRUST_DOCUMENT_UPLOAD",
						guard: ({ context, event }) =>
							context.auth_company_type === "Trust",
					},
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context) &&
							!!context.exemption_requires_both_licenses,
						actions: assign({
							otherCountiesValue: context => undefined,
						}),
					},
					{
						target: "DL_UPLOAD",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
						actions: assign({
							otherCountiesValue: context => undefined,
						}),
					},
				],
			},
		},
		AGREEMENT: {
			entry: [
				assign({
					exemptionsProgress: context => (18 / 19) * 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
				back: [
					{
						target: "OTHER_COUNTIES",
					},
				],
			},
		},
		MAIN_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
	},
	on: {
		update_context: {
			actions: "updateContext",
		},
		restart: {
			target: ".INIT",
		},
	},
});
